/**
 * DemoTour.js — режиссура первых минут демо (Слайс 1: сцены 1–2).
 *
 * Гибрид «рельсы + свобода»: ведёт фаундера от welcome к показательному акту,
 * затем отпускает в свободное исследование.
 *
 * Машина состояний (persist в localStorage, переживает рефреш):
 *   welcome → photos → act → preview → done
 *
 * Связь с UI:
 *   - PropertyDetailPage вешает data-demo-target="showcase-photos" и "act-cta"
 *     на показательную задачу (?demoShowcase=<id>).
 *   - CustomReportButton (демо) рисует инлайн-превью с data-demo-target="act-preview"
 *     и шлёт window-событие 'demo-act-previewed' → тур переходит к финальному коачмарку.
 *
 * Все тексты — i18n (demo.tour.*). PostHog — Analytics.demoTour*.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { Analytics } from '../../analytics';
import { api } from '../../api/client';
import { BRAND_NAME } from '../../branding';
import Spotlight from './Spotlight';
import DemoWelcomeModal from './DemoWelcomeModal';
import TaskModal from '../TaskModal';

const BASE = process.env.REACT_APP_API_URL || '/api';

// Слайс 2 — тайминги «живого» сценария после отправки акта
const SLICE2_OFFER_DELAY_MS = 3000; // пауза ПОСЛЕ закрытия PDF-превью до уведомления «выставить инвойс?»
const SLICE2_PAY_DELAY_MS   = 10000; // пауза от выставления инвойса до «оплачено» (юзер бродит по аппке)
const SLICE3_EXPENSE_DELAY_MS = 12000; // пауза после оплаты «на побродить» до прилёта расхода (слайс 3)

export default function DemoTour() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const orgId = user?.organization_id;
  const storageKey = orgId ? `pm_demo_tour_${orgId}` : null;

  // Загружаем состояние тура из localStorage
  const [state, setStateRaw] = useState(() => {
    if (!storageKey) return { step: 'idle' };
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey));
      if (saved && saved.step) return saved;
    } catch {}
    return { step: 'welcome' };
  });

  // Транслируем текущий шаг тура в баннер (кнопка «Гайд» → «Создать аккаунт» по 'done').
  useEffect(() => {
    if (!user?.is_demo) return;
    window.dispatchEvent(new CustomEvent('demo-tour-step', { detail: { step: state.step, seenConvert: !!state.seenConvert } }));
    // Фиксируем прогресс гайда в Notion (колонка «Статус гайда»). Fire-and-forget.
    api.post('/demo/guide-progress', { step: state.step }).catch(() => {});
  }, [state.step, state.seenConvert, user]);

  // Открыт ли Центр уведомлений (сигнал из Layout) — влияет на позицию плашки offer_bell.
  const [notifCenterOpen, setNotifCenterOpen] = useState(false);
  useEffect(() => {
    const onOpen = () => setNotifCenterOpen(true);
    const onClose = () => setNotifCenterOpen(false);
    window.addEventListener('notif-center-open', onOpen);
    window.addEventListener('notif-center-close', onClose);
    return () => {
      window.removeEventListener('notif-center-open', onOpen);
      window.removeEventListener('notif-center-close', onClose);
    };
  }, []);

  const setState = useCallback((next) => {
    setStateRaw(prev => {
      const merged = typeof next === 'function' ? next(prev) : { ...prev, ...next };
      if (storageKey) {
        try { localStorage.setItem(storageKey, JSON.stringify(merged)); } catch {}
      }
      return merged;
    });
  }, [storageKey]);

  // Рефы для слайса 2 (объявлены рано — на них ссылаются обработчики выше по коду)
  const timersRef = useRef([]);
  const pushTimer = useCallback((id) => { timersRef.current.push(id); }, []);
  const slice2StartedRef = useRef(false);
  const slice3StartedRef = useRef(false); // отсчёт до прилёта расхода запущен (идемпотентность)
  const previewOpenRef = useRef(false);   // PDF-превью акта открыто на экране
  const offerScheduledRef = useRef(false); // отсчёт до оффера уже запущен (идемпотентность)
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);
  useEffect(() => () => { timersRef.current.forEach(clearTimeout); }, []);

  // Запуск отсчёта до оффера инвойса. Стартует ТОЛЬКО когда PDF-превью закрыто
  // (Павел: «оффер доступен, когда превью точно закрыто и прошло 3 секунды»).
  // Идемпотентно: повторные вызовы из разных триггеров безопасны.
  const startOfferCountdown = useCallback(() => {
    if (offerScheduledRef.current) return;
    if (!slice2StartedRef.current) return;     // слайс 2 ещё не стартовал
    if (previewOpenRef.current) return;        // превью открыто — ждём закрытия
    // step здесь не проверяем: setState('await_offer') ещё мог не закоммититься
    // (stateRef обновляется позже). Сам таймер ниже сверяет p.step перед переходом.
    offerScheduledRef.current = true;
    const taskId = stateRef.current.taskId;
    pushTimer(setTimeout(async () => {
      try { await api.post('/demo/emit-invoice-offer', { task_id: taskId }); } catch {}
      Analytics.demoInvoiceOfferShown?.();
      window.dispatchEvent(new CustomEvent('notifications-refresh'));
      setState(p => (['await_offer'].includes(p.step) ? { ...p, step: 'offer_bell' } : p));
    }, SLICE2_OFFER_DELAY_MS));
  }, [pushTimer, setState]);

  // Слайс 3: после оплаты — пауза «на побродить», затем расход прилетает на согласование.
  const scheduleSlice3 = useCallback(() => {
    if (slice3StartedRef.current) return;
    slice3StartedRef.current = true;
    const taskId = stateRef.current.taskId;
    pushTimer(setTimeout(async () => {
      try { await api.post('/demo/emit-expense-offer', { task_id: taskId }); } catch {}
      Analytics.demoExpenseOfferShown?.();
      window.dispatchEvent(new CustomEvent('notifications-refresh'));
      setState(p => ({ ...p, step: 'expense_offer' }));
    }, SLICE3_EXPENSE_DELAY_MS));
  }, [pushTimer, setState]);

  // Аналитика просмотра шага (только в демо)
  useEffect(() => {
    if (!user?.is_demo) return;
    if (['welcome', 'photos', 'act', 'preview'].includes(state.step)) {
      Analytics.demoTourStepViewed?.(state.step);
    }
  }, [state.step, user]);

  // Прячем Action-FAB на время гайда, чтобы стрэй-тап не открыл «Новая Работа».
  // Отдельный demo-lock — переживает открытие/закрытие лайтбокса внутри рельсов.
  useEffect(() => {
    if (!user?.is_demo) return;
    const active = ['welcome', 'loading', 'create_task', 'photos', 'act', 'preview',
      'await_offer', 'offer_bell', 'invoice_issued', 'invoice_paid',
    'expense_offer', 'expense_approved', 'convert'].includes(state.step);
    window.dispatchEvent(new CustomEvent(active ? 'demo-fab-lock' : 'demo-fab-unlock'));
  }, [state.step, user]);
  useEffect(() => {
    if (!user?.is_demo) return undefined;
    return () => window.dispatchEvent(new CustomEvent('demo-fab-unlock'));
  }, [user]);

  // Сигналы из CustomReportButton: превью открыто / закрыто
  useEffect(() => {
    const onPreviewed = () => {
      previewOpenRef.current = true;
      setState(prev => (prev.step === 'act' ? { ...prev, step: 'preview' } : prev));
    };
    const onPreviewClosed = () => {
      // Превью закрыто. Закрытие на шаге акта = акт «отправлен» → запускаем слайс 2.
      // onActSent идемпотентен (ref-гард). После закрытия — стартуем отсчёт до оффера.
      previewOpenRef.current = false;
      const step = stateRef.current.step;
      if (['act', 'preview'].includes(step)) {
        window.dispatchEvent(new CustomEvent('demo-act-sent', { detail: {} }));
      }
      // Если слайс 2 уже стартовал (через «Отправить»), а превью закрыли только сейчас.
      startOfferCountdown();
    };
    // Пользователь сам загрузил фото на шаге «photos» — превращаем коачмарк в успех
    const onPhotoUploaded = (e) => {
      setState(prev => {
        if (prev.step !== 'photos') return prev;
        Analytics.demoTourPhotoAdded?.({ photo_type: e?.detail?.photoType });
        return { ...prev, photoAdded: true };
      });
    };
    window.addEventListener('demo-act-previewed', onPreviewed);
    window.addEventListener('demo-act-preview-closed', onPreviewClosed);
    window.addEventListener('photo-uploaded', onPhotoUploaded);
    return () => {
      window.removeEventListener('demo-act-previewed', onPreviewed);
      window.removeEventListener('demo-act-preview-closed', onPreviewClosed);
      window.removeEventListener('photo-uploaded', onPhotoUploaded);
    };
  }, [setState, startOfferCountdown]);

  // ── Слайс 2: петля «акт отправлен → инвойс → оплата» ──────────────────────────
  // Читаем актуальный шаг через ref, побочки держим в обработчиках (а не в апдейтере
  // setState) — иначе StrictMode задвоил бы dispatch/setTimeout.
  useEffect(() => {
    // 1) Акт «отправлен» (триггеры: «Далее» / «Отправить» / закрытие ✕) → запускаем
    // слайс 2. Идемпотентно (slice2StartedRef) — повторные/программные вызовы безопасны.
    const onActSent = () => {
      if (slice2StartedRef.current) return;
      const prev = stateRef.current;
      if (!['preview', 'act'].includes(prev.step)) return;
      slice2StartedRef.current = true;
      setState(p => ({ ...p, step: 'await_offer' }));
      // PDF-превью НЕ закрываем принудительно. Оффер запустится ТОЛЬКО после того,
      // как юзер закроет превью (✕/«Далее») + пауза 3с — см. startOfferCountdown.
      startOfferCountdown();
    };

    // 2) Юзер нажал ✓ в ЦУ → инвойс выставлен; через паузу — «оплата»
    const onInvoiceIssued = (e) => {
      const prev = stateRef.current;
      if (!['offer_bell', 'await_offer'].includes(prev.step)) return;
      const invoiceId = e?.detail?.invoiceId;
      // Таймер «оплаты» НЕ ставим здесь — он хрупок (теряется при ремоунте/сворачивании
      // вкладки). Переход в invoice_paid ведёт state-driven эффект ниже по invoiceIssuedAt.
      setState(p => ({ ...p, step: 'invoice_issued', invoiceId, invoiceIssuedAt: Date.now() }));
    };

    // 3) Юзер нажал ✗ — мягко завершаем тур
    const onInvoiceDeclined = () => {
      const prev = stateRef.current;
      if (!['offer_bell', 'await_offer'].includes(prev.step)) return;
      Analytics.demoTourCompleted?.();
      setState({ step: 'done' });
    };

    window.addEventListener('demo-act-sent', onActSent);
    window.addEventListener('demo-invoice-issued', onInvoiceIssued);
    window.addEventListener('demo-invoice-declined', onInvoiceDeclined);
    return () => {
      window.removeEventListener('demo-act-sent', onActSent);
      // (deps включают startOfferCountdown)
      window.removeEventListener('demo-invoice-issued', onInvoiceIssued);
      window.removeEventListener('demo-invoice-declined', onInvoiceDeclined);
    };
  }, [setState, pushTimer, startOfferCountdown, scheduleSlice3]);

  // «Оплата» инвойса — управляется состоянием (invoiceIssuedAt), а не одноразовым
  // таймером в обработчике. Переживает ремоунты DemoTour (persist) и сворачивание
  // вкладки: при возврате во вкладку добиваем переход, если задержка уже истекла.
  useEffect(() => {
    if (state.step !== 'invoice_issued') return;
    let done = false;
    const fire = () => {
      if (done) return;
      done = true;
      const { invoiceId, taskId } = stateRef.current;
      (async () => {
        try { if (invoiceId) await api.zohoSyncInvoice(invoiceId); } catch {}
        Analytics.demoInvoicePaid?.();
        window.dispatchEvent(new CustomEvent('demo-invoice-updated', { detail: { taskId } }));
        window.dispatchEvent(new CustomEvent('notifications-refresh'));
        setState(p => (p.step === 'invoice_issued' ? { ...p, step: 'invoice_paid' } : p));
      })();
    };
    const issuedAt = state.invoiceIssuedAt || Date.now();
    const remaining = Math.max(0, SLICE2_PAY_DELAY_MS - (Date.now() - issuedAt));
    const id = setTimeout(fire, remaining);
    const onVis = () => {
      if (document.visibilityState === 'visible' && Date.now() - issuedAt >= SLICE2_PAY_DELAY_MS) fire();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearTimeout(id); document.removeEventListener('visibilitychange', onVis); };
  }, [state.step, state.invoiceIssuedAt, setState]);

  const complete = useCallback(() => {
    Analytics.demoTourCompleted?.();
    setState({ step: 'done' });
  }, [setState]);

  // Слайс 4: конверсия в тенанта. Сбрасываем демо-сессию и уходим на регистрацию
  // с нуля (данные демо не переносим — см. решение «без хвостов»). Полный reload,
  // чтобы приложение поднялось неавторизованным и /register отрисовался.
  const handleConvert = useCallback(() => {
    Analytics.demoConvertClicked?.();
    const params = new URLSearchParams();
    if (user?.email) params.set('email', user.email);
    if (user?.name) params.set('name', user.name);
    const ref = (() => { try { return sessionStorage.getItem('referral_ref'); } catch { return null; } })();
    if (ref) params.set('ref', ref);
    try { localStorage.removeItem('pm_token'); } catch {}
    setState({ step: 'done' });
    window.location.assign(`/register?${params.toString()}`);
  }, [user, setState]);

  // ── Слайс 3: согласование расхода (✓/✗ из ЦУ) ────────────────────────────────
  useEffect(() => {
    const onIssued = () => {
      if (stateRef.current.step !== 'expense_offer') return;
      Analytics.demoExpenseApproved?.();
      window.dispatchEvent(new CustomEvent('notifications-refresh'));
      setState(p => ({ ...p, step: 'expense_approved', expenseDecision: 'approved' }));
    };
    const onDeclined = () => {
      if (stateRef.current.step !== 'expense_offer') return;
      // Отклонить — допустимое действие: показываем ту же гайд-карточку (со своим
      // текстом), затем «Что дальше →» ведёт к конверсии. Не прыгаем сразу.
      Analytics.demoExpenseRejected?.();
      window.dispatchEvent(new CustomEvent('notifications-refresh'));
      setState(p => ({ ...p, step: 'expense_approved', expenseDecision: 'rejected' }));
    };
    window.addEventListener('demo-expense-issued', onIssued);
    window.addEventListener('demo-expense-declined', onDeclined);
    return () => {
      window.removeEventListener('demo-expense-issued', onIssued);
      window.removeEventListener('demo-expense-declined', onDeclined);
    };
  }, [setState, complete]);

  const handleStart = useCallback(async () => {
    Analytics.demoTourStarted?.();
    // Сброс одноразовых guard-рефов — иначе при повторном прогоне (Гайд/хот-релоад)
    // слайсы 2/3 не «вооружатся» заново (scheduleSlice3/startOfferCountdown молча выйдут).
    slice2StartedRef.current = false;
    slice3StartedRef.current = false;
    offerScheduledRef.current = false;
    previewOpenRef.current = false;
    setState(prev => ({ ...prev, step: 'loading' }));
    try {
      const r = await fetch(`${BASE}/demo/tour-target`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('pm_token')}` },
      });
      const data = await r.json().catch(() => ({}));
      if (data?.property_id && data?.task_id) {
        // Слайс 0 — создание/назначение работы (форма предзаполнена данными
        // показательной задачи), затем Слайс 1.
        setState({
          step: 'create_task', propertyId: data.property_id, taskId: data.task_id,
          taskTitle: data.title || '', taskDescription: data.description || '',
          assigneeIds: data.assignee_ids || [],
        });
        navigate(`/properties/${data.property_id}?demoShowcase=${data.task_id}`);
      } else {
        complete(); // нет показательной задачи — мягко выходим
      }
    } catch {
      complete();
    }
  }, [setState, navigate, complete]);

  const handleSkip = useCallback(() => {
    Analytics.demoTourSkipped?.({ at_step: state.step });
    // Запоминаем шаг — чтобы кнопка «Гайд» в баннере могла вернуть юзера сюда же.
    // ВАЖНО spread: иначе теряются propertyId/taskId и возврат на фото/акт ломается.
    setState(p => ({ ...p, step: 'done', resumeStep: p.step }));
  }, [state.step, setState]);

  // Кнопка «Гайд» в демо-баннере: вернуться в тур. Продолжаем с сохранённого шага
  // (resumeStep), если юзер раньше нажал «осмотрюсь сам»; иначе запускаем с начала.
  // Если тур уже активен — игнорируем.
  useEffect(() => {
    const onResume = () => {
      const s = stateRef.current;
      // Видим ли сейчас коачмарк/модалку на экране — тогда Гайд не нужен.
      const visible =
        ['welcome', 'create_task', 'photos', 'act', 'offer_bell', 'expense_offer', 'expense_approved', 'convert'].includes(s.step)
        || (s.step === 'preview' && !s.previewAck)
        || (s.step === 'invoice_issued')
        || (s.step === 'invoice_paid' && !s.paidAck);
      if (visible) return;
      // Пауза «на побродить» после оплаты (paidAck) — возвращаем карточку оплаты,
      // с неё «Далее» снова запустит слайс 3.
      if (s.step === 'invoice_paid' && s.paidAck) {
        setState(p => ({ ...p, paidAck: false }));
        return;
      }
      // Юзер раньше нажал «осмотрюсь сам» — продолжаем с сохранённого шага.
      if (s.resumeStep) {
        const rs = s.resumeStep;
        if (['create_task', 'photos', 'act', 'preview'].includes(rs) && s.propertyId) {
          navigate(`/properties/${s.propertyId}${s.taskId ? `?demoShowcase=${s.taskId}` : ''}`);
        }
        setState(p => ({ ...p, step: rs, resumeStep: undefined }));
        return;
      }
      // Иначе (финал/idle) — запускаем тур заново.
      handleStart();
    };
    window.addEventListener('demo-resume-tour', onResume);
    return () => window.removeEventListener('demo-resume-tour', onResume);
  }, [setState, navigate, handleStart]);

  // ── Гарды ────────────────────────────────────────────────────────────────────
  if (!user?.is_demo) return null;
  if (!['welcome', 'loading', 'create_task', 'photos', 'act', 'preview',
    'await_offer', 'offer_bell', 'invoice_issued', 'invoice_paid',
    'expense_offer', 'expense_approved', 'convert'].includes(state.step)) return null;

  // Сцена 1 — welcome
  if (state.step === 'welcome') {
    return <DemoWelcomeModal onStart={handleStart} onSkip={handleSkip} />;
  }

  // Переход (фетчим цель) — без UI, чтобы не моргать
  if (state.step === 'loading') return null;

  // Шаги, привязанные к элементам показательной страницы (фото/акт/превью), показываем
  // только на ней. Шаги, привязанные к колокольчику/ЦУ (оффер/оплата/расход), —
  // на любой странице: в слайсе 3 юзер бродит по аппке.
  const onShowcasePage = location.pathname === `/properties/${state.propertyId}`;
  if (['create_task', 'photos', 'act', 'preview'].includes(state.step) && !onShowcasePage) return null;

  // Сцена 0 — создание/назначение работы. Форма предзаполнена данными показательной
  // задачи (объект/клиент/бригада), фаундер жмёт «Назначить работу». В демо (вариант A)
  // ничего реально не создаётся — переходим к Слайсу 1 на показательной задаче.
  if (state.step === 'create_task') {
    const today = new Date().toISOString().slice(0, 10);
    return (
      <>
        <TaskModal
          demoMode
          propertyId={state.propertyId}
          demoPrefill={{
            title: state.taskTitle || '', assignee_ids: state.assigneeIds || [],
            scheduled_date: today, type: 'one_time', status: 'pending', planning_type: 'visit',
            scheduled_time: '', date_from: '', reminder_before: 60,
            description: state.taskDescription || '', report_description: '', recurrence: '',
          }}
          demoSubmitLabel={t('demo.tour.slice0.assignBtn')}
          onClose={handleSkip}
          onSave={() => { Analytics.demoTaskAssigned?.(); setState(p => ({ ...p, step: 'photos' })); }}
        />
        {/* Стандартный коачмарк: спотлайт на кнопку «Назначить работу» с плавным
            скроллом к ней. Маски НЕ блокируют скролл (иначе модалку не прокрутить
            к кнопке внизу) — как в остальных слайсах. */}
        <Spotlight
          targetSelector='[data-demo-target="demo-assign-btn"]'
          placement="top"
          zIndex={10006}
          title={t('demo.tour.slice0.title')}
          body={t('demo.tour.slice0.body')}
          secondaryLabel={t('demo.tour.skip')}
          onSecondary={handleSkip}
        />
      </>
    );
  }

  // Сцена 2A — фото. Интерактив: зовём сделать своё фото; после загрузки — успех.
  if (state.step === 'photos') {
    const added = !!state.photoAdded;
    return (
      <Spotlight
        targetSelector={added ? '[data-demo-target="showcase-photos"]' : '[data-demo-target="showcase-before-after"]'}
        title={added ? t('demo.tour.photoAddedTitle') : t('demo.tour.photosTitle')}
        body={added ? t('demo.tour.photoAddedBody') : t('demo.tour.photosBody')}
        ctaLabel={added ? t('demo.tour.photoAddedCta') : t('demo.tour.next')}
        onCta={() => setState({ step: 'act', photoAdded: false })}
        secondaryLabel={t('demo.tour.skip')}
        onSecondary={handleSkip}
        zIndex={1900}
      />
    );
  }

  // Сцена 2B — кнопка акта (ждём реального клика → инлайн-превью)
  if (state.step === 'act') {
    return (
      <Spotlight
        targetSelector='[data-demo-target="act-cta"]'
        title={t('demo.tour.actTitle')}
        body={t('demo.tour.actBody')}
        secondaryLabel={t('demo.tour.skip')}
        onSecondary={handleSkip}
        placement="top"
        zIndex={1900}
      />
    );
  }

  // Сцена 2C — превью акта. Плавающая карточка снизу поверх превью (без подсветки/затемнения).
  // «Далее» НЕ закрывает PDF — только убирает подсказку, чтобы юзер спокойно рассмотрел
  // отчёт. Слайс 2 стартует, когда юзер сам закроет превью (✕/«Отправить») — см. onActSent.
  if (state.step === 'preview' && !state.previewAck) {
    return (
      <Spotlight
        noDim
        title={t('demo.tour.previewTitle')}
        body={t('demo.tour.previewBody')}
        ctaLabel={t('demo.tour.previewDone')}
        onCta={() => setState({ previewAck: true })}
      />
    );
  }

  // ── Слайс 2 ──────────────────────────────────────────────────────────────────
  // Пауза до прихода оффера — без UI.
  if (state.step === 'await_offer') return null;

  // Оффер в ЦУ: подсказываем открыть Центр уведомлений (плавающая карточка, без бэкдропа,
  // чтобы не перекрывать колокольчик и дровер).
  if (state.step === 'offer_bell') {
    // ЦУ закрыт — плашка вверху у колокольчика («загляните»). ЦУ открыт — наводим
    // спотлайт прямо на уведомление (вырез + карточка под ним): уведомление видно
    // и кликабельно (маски pointer-events:none), подсказка его не перекрывает.
    if (notifCenterOpen) {
      return (
        <Spotlight
          targetSelector='[data-demo-target="demo-invoice-notif"]'
          placement="bottom"
          title={t('demo.tour.slice2.offerActionTitle')}
          body={t('demo.tour.slice2.offerActionBody')}
          secondaryLabel={t('demo.tour.skip')}
          onSecondary={handleSkip}
        />
      );
    }
    return (
      <Spotlight
        noDim
        anchor="top"
        title={t('demo.tour.slice2.offerBellTitle')}
        body={t('demo.tour.slice2.offerBellBody')}
        secondaryLabel={t('demo.tour.skip')}
        onSecondary={handleSkip}
      />
    );
  }

  // Инвойс выставлен — подтверждаем, что счёт ушёл клиенту на почту (и фаундеру в копию).
  // Через паузу по таймеру прилетит «оплачено» (колокольчик + ЦУ + email).
  if (state.step === 'invoice_issued') {
    return (
      <Spotlight
        noDim
        title={t('demo.tour.slice2.issuedTitle')}
        body={t('demo.tour.slice2.issuedBody')}
      />
    );
  }

  // Оплачено — финал слайса 2. CTA НЕ завершает тур: впереди слайс 3 (расход),
  // который уже стартовал по таймеру. «Осмотрюсь сам» просто прячет карточку —
  // юзер бродит, пока через паузу не прилетит расход на согласование.
  if (state.step === 'invoice_paid' && !state.paidAck) {
    return (
      <Spotlight
        noDim
        title={t('demo.tour.slice2.paidTitle')}
        body={t('demo.tour.slice2.paidBody')}
        ctaLabel={t('demo.tour.slice2.paidCta')}
        onCta={() => { scheduleSlice3(); setState(p => ({ ...p, paidAck: true })); }}
      />
    );
  }

  // ── Слайс 3 — Расходы ─────────────────────────────────────────────────────────
  // Расход прилетел на согласование. Плашка у колокольчика (как в слайсе 2), при
  // открытом ЦУ — уезжает вниз и зовёт нажать ✓.
  if (state.step === 'expense_offer') {
    if (notifCenterOpen) {
      return (
        <Spotlight
          targetSelector='[data-demo-target="demo-expense-notif"]'
          placement="bottom"
          title={t('demo.tour.slice3.offerActionTitle')}
          body={t('demo.tour.slice3.offerActionBody')}
          secondaryLabel={t('demo.tour.skip')}
          onSecondary={handleSkip}
        />
      );
    }
    return (
      <Spotlight
        noDim
        anchor="top"
        title={t('demo.tour.slice3.offerBellTitle')}
        body={t('demo.tour.slice3.offerBellBody')}
        secondaryLabel={t('demo.tour.skip')}
        onSecondary={handleSkip}
      />
    );
  }

  // Расход обработан (согласован/отклонён) — финал слайса 3, мостик в конверсию.
  if (state.step === 'expense_approved') {
    const rejected = state.expenseDecision === 'rejected';
    return (
      <Spotlight
        noDim
        title={t(rejected ? 'demo.tour.slice3.rejectedTitle' : 'demo.tour.slice3.approvedTitle')}
        body={t(rejected ? 'demo.tour.slice3.rejectedBody' : 'demo.tour.slice3.approvedBody')}
        ctaLabel={t('demo.tour.slice3.approvedCta')}
        onCta={() => { Analytics.demoConvertShown?.(); setState(p => ({ ...p, step: 'convert', seenConvert: true })); }}
      />
    );
  }

  // ── Слайс 4 — Конверсия в тенанта (кульминация) ──────────────────────────────
  if (state.step === 'convert') {
    return (
      <div
        data-ph-capture-attribute-component="demo_convert_modal"
        style={{
          position: 'fixed', inset: 0, zIndex: 10001, background: 'rgba(0,0,0,0.62)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}
      >
        <div style={{
          background: 'var(--bg2)', borderRadius: 18, maxWidth: 440, width: '100%',
          maxHeight: '90vh', overflowY: 'auto',
          padding: '30px 26px 22px', textAlign: 'center', boxShadow: '0 18px 64px rgba(0,0,0,0.36)',
        }}>
          <div style={{ fontSize: 44, marginBottom: 8 }}>🚀</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', marginBottom: 10 }}>
            {t('demo.tour.slice4.title')}
          </div>
          <div style={{ fontSize: 15, color: 'var(--text2)', lineHeight: 1.55, marginBottom: 16 }}>
            {t('demo.tour.slice4.body', { brand: BRAND_NAME })}
          </div>

          {/* Вектор развития — тизер бэклога */}
          <div style={{
            textAlign: 'left', background: 'var(--bg2, #f5f5f7)', borderRadius: 12,
            padding: '12px 14px', marginBottom: 16,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 8 }}>
              {t('demo.tour.slice4.roadmapTitle', { brand: BRAND_NAME })}
            </div>
            {['roadmap1', 'roadmap2', 'roadmap3', 'roadmap4', 'roadmap5'].map(k => (
              <div key={k} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, color: 'var(--text)', lineHeight: 1.4, marginBottom: 5 }}>
                {k !== 'roadmap5' && <span style={{ color: 'rgba(99,102,241,1)' }}>✦</span>}
                <span>{t(`demo.tour.slice4.${k}`)}</span>
              </div>
            ))}
          </div>

          {/* Цена — ответ на «сколько стоит» перед CTA */}
          <div style={{
            background: 'rgba(99,102,241,0.10)', borderRadius: 12, padding: '12px 14px',
            marginBottom: 18, fontSize: 14, color: 'var(--text)', lineHeight: 1.5,
          }}>
            {t('demo.tour.slice4.priceLine')}
          </div>

          <button
            onClick={handleConvert}
            data-ph-capture-attribute-action="demo_convert"
            style={{
              width: '100%', background: 'rgba(99,102,241,1)', color: '#fff', border: 'none',
              padding: '14px 16px', borderRadius: 11, fontSize: 16, fontWeight: 700, cursor: 'pointer',
            }}
          >
            {t('demo.tour.slice4.cta')}
          </button>
          <button
            onClick={() => {
              Analytics.demoConvertDismissed?.();
              // Закрываем ЦУ и показываем Работу, с которой играли в демо.
              window.dispatchEvent(new CustomEvent('demo-close-notif-center'));
              if (state.propertyId) {
                navigate(`/properties/${state.propertyId}`, state.taskId ? { state: { openTaskId: Number(state.taskId) } } : undefined);
              }
              setState(p => ({ ...p, step: 'done', resumeStep: 'convert' }));
            }}
            data-ph-capture-attribute-action="demo_convert_dismiss"
            style={{
              marginTop: 10, background: 'none', border: 'none', color: 'var(--text3, #888)',
              fontSize: 13, cursor: 'pointer', textDecoration: 'underline',
            }}
          >
            {t('demo.tour.slice4.secondary')}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
